import { Form, Formik } from "formik";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";

import { BusinessRounded } from "@mui/icons-material";
import InfoIcon from "@mui/icons-material/Info";
import SaveIcon from "@mui/icons-material/Save";
import { Box, Button, Typography } from "@mui/material";

import { PortfolioAnalyticsService } from "@analytics/index";

import TextField from "@components/Forms/FormInputs/TextField";
import {
  TableToolbar,
  TableToolbarLeft,
  TableToolbarRight,
  TableToolbarTitle,
} from "@components/TableToolbar/TableToolbar";

import routes from "@constants/routes";

import { usePortfolioQuery, useUpdateTechnologyQuery } from "@queries/portfolio.query";

import useSmartSearchStore from "@store/smartSearch";

import contentCardStyles from "@theme/contentCardStyles";

interface ITechnologyOverviewProperties {
  technologyDescription?: string;
  technologyId: string;
  technologyName: string;
}

export default function TechnologyDetails({
  technologyDescription,
  technologyId,
  technologyName,
}: ITechnologyOverviewProperties) {
  const { data: portfolios } = usePortfolioQuery();
  const navigate = useNavigate();

  const { setIsSmartSearchFromTechnologyOverview, setTechnologyDescription } = useSmartSearchStore();

  const { mutateAsync: updateTechnology } = useUpdateTechnologyQuery();

  const existingNames: string[] = [];
  if (portfolios) {
    let existingPortfolios;
    if ("portfolio" in portfolios && Array.isArray(portfolios.portfolio)) {
      existingPortfolios = portfolios.portfolio;
    } else if (Array.isArray(portfolios)) {
      existingPortfolios = portfolios;
    } else {
      throw new TypeError("Invalid portfolios structure");
    }
  }

  const createTechnologySchema = Yup.object().shape({
    description: Yup.string().nullable(),
    name: Yup.string().required("Required").notOneOf(existingNames, "Technology with this name already exists."),
  });

  const handleSubmit = async (values: { description?: string; id: string; name: string }) => {
    await updateTechnology({
      newDescription: values.description,
      newName: values.name,
      technologyId: values.id,
    });
  };

  const triggerSmartSearch = (values: { description?: string; name: string }) => {
    setTechnologyDescription(values.description);
    setIsSmartSearchFromTechnologyOverview(true);
    PortfolioAnalyticsService.initiateSmartSearchFromTechnology(technologyId, values.name, values.description);
    navigate(`${routes.EMBEDDING_SEARCH}/companies`);
  };

  return (
    <Formik
      initialValues={{
        description: technologyDescription,
        id: technologyId,
        name: technologyName,
      }}
      onSubmit={handleSubmit}
      sx={{ height: "100%" }}
      validationSchema={createTechnologySchema}
    >
      {({ dirty, handleChange, handleSubmit, values }) => (
        <Box component={Form} sx={{ ...contentCardStyles }}>
          <TableToolbar>
            <TableToolbarLeft>
              <TableToolbarTitle
                resourceLink=""
                tableSubtitle="Add key details about your technology to improve your search and outreach efforts."
                tableTitle="Technology Details"
                workflow=""
              />
            </TableToolbarLeft>
            <TableToolbarRight>
              <Button
                color="secondary"
                onClick={() => triggerSmartSearch(values)}
                startIcon={<BusinessRounded />}
                sx={{ borderRadius: "8px", fontColor: "white" }}
                variant="contained"
              >
                Find Companies
              </Button>
              <Button
                disabled={!dirty} // Enable button only if dirty
                onClick={() => handleSubmit()} // Use Formik's submit handling directly
                startIcon={<SaveIcon />}
                sx={{ borderRadius: "8px", fontColor: "white" }}
                variant="contained"
              >
                Update
              </Button>
            </TableToolbarRight>
          </TableToolbar>
          <Box sx={{ padding: "20px" }}>
            <Typography sx={{ fontSize: 16, fontWeight: "bold" }} variant="body1">
              Name
            </Typography>
            <TextField
              color="secondary"
              handleKeyDown={handleChange}
              inputProperties={{ maxLength: "90", minLength: "1" }}
              isFullWidth
              isRequired
              label=""
              name="name"
              size="medium"
              type="text"
            />

            <Box
              sx={{
                flexGrow: 1,
                height: "100%", // Adjust this as needed
                marginTop: "20px",
              }}
            >
              <Typography
                sx={{ display: "inline-block", fontSize: 16, fontWeight: "bold", position: "relative", top: "20px" }}
                variant="body1"
              >
                Description
              </Typography>
              <Box display="flex" justifyContent="flex-end">
                <InfoIcon sx={{ color: "#0288D1", fontSize: "18px", marginRight: "5px" }} />
                <Typography sx={{ fontSize: 12 }} variant="body1">
                  All information is private, secure, and{" "}
                  <Box component="span" sx={{ fontWeight: "bold" }}>
                    {" "}
                    never{" "}
                  </Box>{" "}
                  used for model training.
                </Typography>
              </Box>
              <TextField
                color="secondary"
                handleKeyDown={handleChange}
                isFullWidth
                isMultiline={true}
                label=""
                name="description"
                rows={10}
                size="medium"
                sx={{
                  height: "100% !important",
                  mt: "20px",
                }}
                type="text"
              />
            </Box>
          </Box>
        </Box>
      )}
    </Formik>
  );
}
